<template>
  <li>
    <div class="d-flex align-items-center tree-item-header">
      <div
        @click="handleClick"
        class="d-flex align-items-center cursor-pointer"
      >
        <b-spinner small v-if="item.loading" class="mr-2"></b-spinner>
        <feather-icon
          v-if="item.isDirectory"
          :icon="item.isExpanded ? 'FolderMinusIcon' : 'FolderIcon'"
          size="16"
          class="mr-1"
        />
        <feather-icon v-else :icon="fileIcon" size="16" class="mr-1" />
        <span :class="{ 'font-weight-bold': item.isExpanded }">{{
          item.name
        }}</span>

        <!-- tooltips -->
        <span
          v-if="item.totalRecords !== undefined"
          class="text-muted ml-1 counts"
        >
          <span
            class="mr-1 custom-tooltip"
            :data-tooltip="$t('subdirectories_and_direct_files')"
          >
            ({{ item.totalRecords }})
          </span>
          <span class="mr-1 custom-tooltip" :data-tooltip="$t('direct_files')">
            ({{ item.totalFiles }})
          </span>
          <span
            class="custom-tooltip"
            :data-tooltip="$t('files_in_subdirectory')"
          >
            ({{ item.totalSubdirectoriesFiles }})
          </span>
        </span>
      </div>

      <!-- search icon just to expanded folders -->
      <feather-icon
        v-if="item.isDirectory && item.isExpanded"
        icon="SearchIcon"
        size="16"
        class="ml-2 cursor-pointer"
        @click="toggleFilter"
      />
    </div>

    <ul v-if="item.isExpanded && item.isDirectory" class="no-bullets">
      <!-- filters -->
      <div v-if="showFilter" class="filter-controls">
        <b-input-group size="sm">
          <b-form-input
            v-model="filterName"
            placeholder="Filtrar por nombre"
            @keyup.enter="applyFilter"
          ></b-form-input>
          <b-input-group-append>
            <b-button @click="applyFilter" variant="primary">Aplicar</b-button>
          </b-input-group-append>
        </b-input-group>
      </div>

      <tree-item
        v-for="child in item.children"
        :key="child.path"
        :item="child"
        @fetchChildren="handleFetchChildren"
      />

      <div
        v-if="item.totalPages > 1"
        class="pagination-controls d-flex justify-content-center mt-2"
      >
        <b-pagination
          v-model="item.page"
          :total-rows="item.totalRecords"
          :per-page="item.per_page"
          :limit="3"
          size="sm"
          @change="pageChanged"
          first-number
          last-number
        ></b-pagination>
      </div>
    </ul>
  </li>
</template>

<script>
import { defineComponent, computed, ref } from "@vue/composition-api";
import FeatherIcon from "@core/components/feather-icon/FeatherIcon.vue";
import TreeItem from "./TreeItem.vue";
import {
  BSpinner,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BButton,
  BPagination,
} from "bootstrap-vue";

export default defineComponent({
  name: "TreeItem",
  components: {
    FeatherIcon,
    TreeItem,
    BSpinner,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BPagination,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    const showFilter = ref(false);

    const toggleFilter = () => {
      showFilter.value = !showFilter.value;
      if (showFilter.value && !props.item.isExpanded) {
        expandDirectory();
      }
    };

    const expandDirectory = () => {
      if (props.item.loading) return;

      if (props.item.isExpanded) {
        props.item.isExpanded = false;
      } else {
        if (props.item.children.length === 0) {
          props.item.loading = true;
          emit(
            "fetchChildren",
            props.item,
            props.item.page,
            props.item.per_page || 10,
            props.item.filter || {}
          );
        } else {
          props.item.isExpanded = true;
        }
      }
    };

    const handleClick = () => {
      if (props.item.isDirectory) {
        expandDirectory();
      } else if (props.item.url) {
        window.open(props.item.url, "_blank");
      }
    };

    const fileIcon = computed(() => {
      if (props.item.isDirectory)
        return props.item.isExpanded ? "FolderMinusIcon" : "FolderIcon";
      const extension = props.item.name.split(".").pop().toLowerCase();
      const iconMap = {
        jpg: "ImageIcon",
        jpeg: "ImageIcon",
        png: "ImageIcon",
        gif: "ImageIcon",
        pdf: "FileTextIcon",
        doc: "FileTextIcon",
        docx: "FileTextIcon",
        xls: "FileTextIcon",
        xlsx: "FileTextIcon",
        ppt: "FileTextIcon",
        pptx: "FileTextIcon",
        txt: "FileTextIcon",
        mp3: "MusicIcon",
        mp4: "VideoIcon",
        avi: "VideoIcon",
        mkv: "VideoIcon",
      };
      return iconMap[extension] || "FileIcon";
    });

    // Filter Controls
    const filterName = ref(props.item.filter ? props.item.filter.name : "");

    const applyFilter = () => {
      props.item.loading = true;
      const newFilter = {
        ...props.item.filter,
        name: filterName.value,
      };
      emit(
        "fetchChildren",
        props.item,
        1,
        props.item.per_page || 10,
        newFilter
      );
    };

    // Pagination Controls
    const pageChanged = (newPage) => {
      props.item.loading = true;
      emit(
        "fetchChildren",
        props.item,
        newPage,
        props.item.per_page,
        props.item.filter || {}
      );
    };

    // Manage fetchChildren from childs
    const handleFetchChildren = (...args) => {
      emit("fetchChildren", ...args);
    };

    return {
      handleClick,
      fileIcon,
      filterName,
      applyFilter,
      pageChanged,
      handleFetchChildren,
      showFilter,
      toggleFilter,
    };
  },
});
</script>

<style scoped>
.tree-item-header {
  display: flex;
  align-items: center;
}
.cursor-pointer {
  cursor: pointer;
}
.ml-2 {
  margin-left: 0.5rem;
}
.text-muted {
  color: #6c757d;
}
.counts .custom-tooltip {
  position: relative;
  cursor: pointer;
}
.counts .custom-tooltip::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 125%; /* Position above the element */
  left: 50%;
  transform: translateX(-50%);
  background-color: #333;
  color: #fff;
  padding: 5px;
  border-radius: 4px;
  white-space: nowrap;
  font-size: 12px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease-in-out;
  z-index: 10;
}
.counts .custom-tooltip:hover::after {
  opacity: 1;
}
.filter-controls {
  margin: 10px 0;
}
.pagination-controls {
  margin-top: 10px;
}
.font-weight-bold {
  font-weight: bold;
}
.no-bullets {
  list-style-type: none;
  padding-left: 20px; /* keep indentation */
  margin-left: 0;
}
</style>
